/**
 * What the same diamonds are worth under different position-management rules.
 *
 * The signals are fixed; what changes is what you do when one arrives while
 * you are already in a trade. Four answers, and the gaps between them are the
 * whole point:
 *
 *   ALL         every signal judged on its own, no position tracking at all.
 *               Answers "how good is the rule", not "what could I have traded".
 *   SKIP        the live bot rule (`skip_if_positioned`, maxConcurrent 1).
 *               Anything arriving mid-trade is lost.
 *   FLIP        an OPPOSITE signal closes the position at that signal's price
 *               and reverses at the same size. Same-direction signals are still
 *               absorbed — you are already positioned that way.
 *   MARTINGALE  as FLIP, but size doubles after every losing close and snaps
 *               back to base on a win.
 *
 * Measured over 24h of live MNQ diamonds, FLIP beat SKIP by roughly $370 and
 * halved the drawdown, mostly because SKIP sits out ~28 tradeable reversals.
 * MARTINGALE edged FLIP on net while reaching 8 contracts off just TWO
 * consecutive losses — which is why `peakQuantity` is reported rather than
 * buried. Deliberately uncapped here: the overlay's job is to show the tail
 * honestly, and a cap would hide the number worth being frightened of.
 *
 * All of it is computed from the `simulated` block already on each diamond, so
 * there is no second implementation of "did this hit TP or SL first".
 */

import type { EngineDiamond } from './setupCandidates';

export type PositionModel = 'all' | 'skip' | 'flip' | 'martingale';

export const POSITION_MODELS: readonly PositionModel[] = ['all', 'skip', 'flip', 'martingale'];

export interface ModelTrade {
  /** Signed points per contract. */
  points: number;
  quantity: number;
  dollars: number;
  grade: string;
  /** A flat scratch counts as a loss — it did not go your way. */
  won: boolean;
  /** Timestamp of the signal that OPENED this position.
   *
   *  Carried so a single signal's popup can report what the policy actually
   *  did with it, instead of the standalone bracket walk the engine attaches.
   *  Those two disagree by design — one asks "was the signal right", the other
   *  "what would the bot have made" — and showing only the first is what makes
   *  a flipped-out trade read as still open. */
  openedAt: number;
  /** How the position ended. */
  closedBy: 'bracket' | 'reversal';
  /** Price it was closed at when a reversal ended it, else null. */
  closedAt: number | null;
}

export interface ModelOutcome {
  trades: ModelTrade[];
  /** Signals lost because the account was already positioned. */
  skipped: number;
  /**
   * Resting entries that never filled. Not trades, and NOT losses — price
   * simply never came back to the limit before it expired.
   */
  unfilled: number;
  /** Largest position held. Only ever above base under `martingale`. */
  peakQuantity: number;
  /** Trades whose bar touched both levels; unscoreable from OHLC. */
  ambiguous: number;
}

/** MNQ. Only used if the window has no resolved trade to derive it from. */
const FALLBACK_DOLLARS_PER_POINT = 2;

/**
 * Dollars per point per contract, read back out of the engine's own figures.
 *
 * A flipped trade exits somewhere the engine never simulated, so its dollar
 * value has to be computed here — and hardcoding a multiplier would silently
 * be wrong on any instrument but MNQ. `dollars = points * $/pt * quantity`, so
 * one resolved trade is enough to recover it.
 */
export function dollarsPerPoint(diamonds: readonly EngineDiamond[]): number {
  for (const d of diamonds) {
    const s = d.simulated;
    if (s && s.points !== 0 && s.quantity > 0) {
      const v = s.dollars / (s.points * s.quantity);
      if (Number.isFinite(v) && v > 0) return v;
    }
  }
  return FALLBACK_DOLLARS_PER_POINT;
}

interface OpenPosition {
  long: boolean;
  entry: number;
  quantity: number;
  /** The signal that opened it — carries its own simulated exit. */
  source: EngineDiamond;
}

/**
 * Run `sorted` (ascending by timestamp) through one position model.
 *
 * Diamonds whose simulated order never filled are counted and skipped: they
 * are not trades. Folding them into wins is the exact bug that had the med
 * tier reading better than it was, and it biases one way — with a small
 * target, a signal that never retraces is usually one that ran straight to it.
 */
export function runPositionModel(
  sorted: readonly EngineDiamond[],
  model: PositionModel,
  perPoint: number
): ModelOutcome {
  const trades: ModelTrade[] = [];
  let pos: OpenPosition | null = null;
  let multiplier = 1;
  let peakQuantity = 0;
  let skipped = 0;
  let unfilled = 0;
  let ambiguous = 0;

  const book = (
    points: number,
    quantity: number,
    grade: string,
    openedAt: number,
    closedBy: 'bracket' | 'reversal',
    closedAt: number | null
  ) => {
    const won = points > 0;
    trades.push({
      points,
      quantity,
      dollars: points * perPoint * quantity,
      grade,
      won,
      openedAt,
      closedBy,
      closedAt,
    });
    if (model === 'martingale') multiplier = won ? 1 : multiplier * 2;
  };

  /** Close `pos` if its own bracket resolved at or before `at`. */
  const settleIfResolved = (at: number) => {
    if (!pos) return;
    const s = pos.source.simulated!;
    const exit = s.exitTimestamp;
    if (exit !== null && exit !== undefined && exit <= at) {
      book(s.points, pos.quantity, pos.source.grade, pos.source.timestamp, 'bracket', null);
      pos = null;
    }
  };

  for (const d of sorted) {
    const sim = d.simulated;
    if (!sim) continue;
    if (sim.result === 'unfilled') {
      unfilled++;
      continue;
    }
    // Touched both levels in one bar: it traded, but which side hit first is
    // not knowable from OHLC. Booking it either way is a guess, and at a
    // 17-point bracket there are enough of them to move the win rate several
    // points -- which is exactly how the scoreboard came to disagree with tick
    // order about whether this bracket makes money.
    if (sim.result === 'ambiguous') {
      ambiguous++;
      continue;
    }

    // ALL ignores position state entirely -- every signal stands alone.
    if (model === 'all') {
      book(sim.points, Math.max(1, sim.quantity), d.grade, d.timestamp, 'bracket', null);
      peakQuantity = Math.max(peakQuantity, Math.max(1, sim.quantity));
      continue;
    }

    const long = d.direction === 'long';
    settleIfResolved(d.timestamp);

    if (!pos) {
      const quantity = Math.max(1, sim.quantity) * (model === 'martingale' ? multiplier : 1);
      pos = { long, entry: sim.entry, quantity, source: d };
      peakQuantity = Math.max(peakQuantity, quantity);
      continue;
    }

    // Already positioned the same way: nothing to do under any model.
    if (pos.long === long || model === 'skip') {
      skipped++;
      continue;
    }

    // Reverse: realise the open trade at THIS signal's entry, not at its own
    // bracket, then turn around. `book` has already resized for the next one.
    const points = pos.long ? sim.entry - pos.entry : pos.entry - sim.entry;
    book(points, pos.quantity, pos.source.grade, pos.source.timestamp, 'reversal', sim.entry);
    const quantity = Math.max(1, sim.quantity) * (model === 'martingale' ? multiplier : 1);
    pos = { long, entry: sim.entry, quantity, source: d };
    peakQuantity = Math.max(peakQuantity, quantity);
  }

  // Whatever is still held: book it only if its bracket ever resolved. An
  // unresolved position is not a result, so it is left out rather than
  // guessed at.
  if (pos) {
    const s = pos.source.simulated!;
    if (s.exitTimestamp !== null && s.exitTimestamp !== undefined) {
      book(s.points, pos.quantity, pos.source.grade, pos.source.timestamp, 'bracket', null);
    }
  }

  return { trades, skipped, unfilled, ambiguous, peakQuantity };
}

/**
 * Peak-to-trough of the running dollar curve, as a positive number.
 *
 * Dollars rather than points, because under martingale the size varies and a
 * points curve would understate a deep hole taken at eight contracts.
 */
export function maxDrawdown(inOrder: readonly number[]): number {
  let equity = 0;
  let peak = 0;
  let worst = 0;
  for (const v of inOrder) {
    equity += v;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > worst) worst = dd;
  }
  return worst;
}

/**
 * The lowest the running total ever reached — how far underwater you actually
 * went, measured from zero rather than from a prior high.
 *
 * NOT the same as drawdown, and the difference matters. A $336 drawdown on a
 * curve that had climbed to $308 leaves you at -$28, not -$336; drawdown says
 * how much you gave back, this says how deep the hole under the starting line
 * ever got. Zero means never underwater.
 *
 * Returned as a signed number (negative when underwater) so a caller cannot
 * render it as a loss when there wasn't one.
 */
export function lowWaterMark(inOrder: readonly number[]): number {
  let equity = 0;
  let lowest = 0;
  for (const v of inOrder) {
    equity += v;
    if (equity < lowest) lowest = equity;
  }
  return lowest;
}

/**
 * What `model` did with the position a single signal opened.
 *
 * `null` when that signal never opened one — it was absorbed while already
 * positioned the same way, skipped as busy, or its resting order expired.
 * Distinguishing "no position" from "position still running" is the whole
 * point: the engine's per-signal block calls both of those `open`.
 */
export function outcomeForSignal(
  sorted: readonly EngineDiamond[],
  model: PositionModel,
  perPoint: number,
  timestamp: number
): ModelTrade | null {
  const { trades } = runPositionModel(sorted, model, perPoint);
  return trades.find((t) => t.openedAt === timestamp) ?? null;
}
